// Regras puras da tela do Plano de trabalho (sem antd, testáveis no jest do CRA).
// O servidor confere tudo de novo (handlers/planoTrabalho*.js); aqui a regra
// serve para AVISAR antes, em frase para gente, e não para decidir.
import type { EtapaPlanoTrabalho, PlanoTrabalhoSnapshot } from './types';

type Usuario = PlanoTrabalhoSnapshot['usuarios'][number];
type AtividadePlano = PlanoTrabalhoSnapshot['atividades'][number];

export const ROTULOS_FONTE: Record<string, string> = {
  objetivos: 'Objetivos',
  requisitos_especificos: 'Requisitos específicos',
  restricoes_tecnicas: 'Restrições técnicas',
  dependencias_terceiros: 'Dependências de terceiros',
  cronograma_texto: 'Cronograma previsto',
  entregas_texto: 'Entregas previstas',
};

export const ROTULO_PAPEL: Record<string, string> = { gestor: 'Gestor', coordenador: 'Coordenador' };

export const ROTULO_STATUS_ATIVIDADE: Record<string, string> = {
  pendente: 'Pendente', em_andamento: 'Em andamento', concluida: 'Concluída',
  bloqueada: 'Bloqueada', cancelada: 'Cancelada',
};

const normEmail = (v: string | null | undefined) => String(v || '').toLowerCase().trim();

/** O que falta nesta etapa para o plano poder ser aprovado. Lista vazia = pronta. */
export function pendenciasEtapa(etapa: EtapaPlanoTrabalho, usuarios: Usuario[]): string[] {
  const faltas: string[] = [];
  if (!etapa.titulo.trim()) faltas.push('título');
  if (!etapa.responsavel_id) faltas.push('responsável');
  else if (!usuarios.some(u => u.id === etapa.responsavel_id)) faltas.push('responsável da equipe atual');
  if (!etapa.data_fim) faltas.push('data final');
  if (!etapa.resultado_esperado.trim()) faltas.push('resultado esperado');
  if (!etapa.criterio_aceite.trim()) faltas.push('critério de aceite');
  if (etapa.data_inicio && etapa.data_fim && etapa.data_inicio > etapa.data_fim) faltas.push('fim depois do início');
  return faltas;
}

/**
 * Diferenças entre a etapa e a atividade já cadastrada à qual ela está ligada.
 * O servidor recusa aprovar com qualquer diferença (o plano não reescreve
 * trabalho existente), então a tela mostra antes.
 */
export function divergenciasVinculo(
  etapa: EtapaPlanoTrabalho, atividade: AtividadePlano | undefined,
  usuarios: Usuario[], etapas: EtapaPlanoTrabalho[],
): string[] {
  if (!etapa.atividade_id) return [];
  if (!atividade) return ['a atividade ligada não existe mais neste contrato'];
  const difs: string[] = [];
  if ((atividade.data_inicio_prevista || null) !== (etapa.data_inicio || null)) difs.push('data de início');
  if ((atividade.data_termino_previsto || null) !== (etapa.data_fim || null)) difs.push('data final');
  const pessoa = usuarios.find(u => u.id === etapa.responsavel_id);
  const responsavelConfere = (atividade.atribuicoes || [])
    .some(a => a.papel === 'responsavel' && normEmail(a.email) === normEmail(pessoa?.email));
  if (!responsavelConfere) difs.push('responsável');
  const previstas = etapa.dependencias.map(id => etapas.find(e => e.id === id)?.atividade_id || null);
  const iguais = JSON.stringify(Array.from(new Set(previstas)).sort())
    === JSON.stringify(Array.from(new Set(atividade.predecessoras || [])).sort());
  if (previstas.some(id => !id) || !iguais) difs.push('dependências');
  return difs;
}

/**
 * Ao escolher uma atividade existente, copia para a etapa o que o servidor exige
 * que bata (datas, responsável e dependências), e diz o que não conseguiu copiar.
 */
export function ligarAtividadeExistente(
  etapa: EtapaPlanoTrabalho, atividade: AtividadePlano,
  usuarios: Usuario[], etapas: EtapaPlanoTrabalho[],
): { etapa: EtapaPlanoTrabalho; avisos: string[] } {
  const avisos: string[] = [];
  const emailResp = (atividade.atribuicoes || []).find(a => a.papel === 'responsavel')?.email;
  const pessoa = usuarios.find(u => normEmail(u.email) === normEmail(emailResp));
  if (!emailResp) avisos.push('a atividade não tem responsável; defina um nela antes de aprovar');
  else if (!pessoa) avisos.push(`o responsável da atividade (${emailResp}) não está na equipe ativa do contrato`);
  const dependencias: string[] = [];
  for (const pred of atividade.predecessoras || []) {
    const origem = etapas.find(e => e.id !== etapa.id && e.atividade_id === pred);
    if (origem) dependencias.push(origem.id);
    else avisos.push('uma atividade anterior da qual ela depende não está ligada a nenhuma etapa');
  }
  return {
    etapa: {
      ...etapa,
      atividade_id: atividade.id,
      data_inicio: atividade.data_inicio_prevista || null,
      data_fim: atividade.data_termino_previsto || null,
      responsavel_id: pessoa ? pessoa.id : etapa.responsavel_id,
      dependencias,
    },
    avisos,
  };
}

/** Remove a etapa e tira o nome dela das dependências das outras. */
export function removerEtapa(etapas: EtapaPlanoTrabalho[], id: string): EtapaPlanoTrabalho[] {
  return etapas.filter(e => e.id !== id).map(e => ({ ...e, dependencias: e.dependencias.filter(d => d !== id) }));
}

/** Mensagem de erro para gente: a do servidor quando há; sem rede, diz isso. */
export function mensagemErro(e: any): string {
  const doServidor = e?.response?.data?.error || e?.response?.data?.message;
  if (doServidor) return String(doServidor);
  if (e?.response?.status === 504) return 'O servidor demorou para responder. Recarregue o plano para conferir o que ficou gravado.';
  if (!e?.response) return 'Sem conexão com o servidor. Confira a internet e tente de novo.';
  return 'A operação não pôde ser concluída. Recarregue o plano e tente de novo.';
}
